<template>
  <div class="adverse-events-chart graphic-container" :class="{ 'graphic-container--social-mode': this.socialMode }">
    <h3 class="chart-hed" v-if="showHeading">Medical devices potentially linked to almost 83,000 deaths</h3>
    <p v-if="showDeck">ICIJ analysis of data from the United States has also found more than 1.7 million injuries potentially linked to medical devices.</p>
    <ul class="chart-legend">
      <li v-for="(series, i) in chartOptions.seriesNames" :key="i"><span class="chart-legend__box" :style="{'background-color': colorScale(series)}"></span>{{ series[0].toUpperCase() + series.substring(1) }}</li>
    </ul>
    <svg :width="width" :height="height" class="hide-x-axis-ticks">
      <g :style="{transform: `translate(${margin.left}px, ${margin.top}px)`}">
        <!-- x-axis -->
        <g :id="chartOptions.chartName + '__axis--x'" class="axis axis--x" :style="{transform: `translate(0, ${padded.height}px)`}"></g>

        <!-- y-axis -->
        <g :id="chartOptions.chartName + '__axis--y'" class="axis axis--y" :style="{transform: `translate(${padded.left}px, 0)`}"></g>

        <!-- grouped bars -->
        <g v-for="(series, i) in formattedData" :key="i">
          <rect v-for="(d, j) in series" :key="j" :x="scale.x(d.date) + Math.abs(scale.x.bandwidth() / 2 * (i))" :y="i === 0 ? scale.y(d.value) : scale.y(d.value)" :width="Math.abs(scale.x.bandwidth() / 2)" :height="Math.abs(padded.height - scale.y(d.value))" :fill="colorScale(i)"></rect>
        </g>
      </g>
    </svg>
    <p class="chart-source">Source: U.S. Food and Drug Administration, ICIJ analysis</p>
  </div>
</template>

<script>
  import data from '../assets/fda-adverse-events.json'
  import isFunction from 'lodash/isFunction'
  import identity from 'lodash/identity'
  import * as d3 from 'd3';
  import embeddable from '../mixins/embeddable'

  const castCall = (fnOrValue = identity, ...rest) => isFunction(fnOrValue) ? fnOrValue(...rest) : fnOrValue

  const chartOptions = {
    chartName: "adverseEvents",
    seriesNames: ["injuries", "deaths"],
    labelWidth: "60",
    yAxisTickFormat: d => d === 0 ? d : d3.format(",.2r")(d),
    yAxisTicks: 5,
    colors: ['#F8B700', '#A83324'],
  },
  colorScale = d3.scaleOrdinal()
    .domain(chartOptions.seriesNames)
    .range(chartOptions.colors),
  formattedData = chartOptions.seriesNames.map(function(key) {
    return data.map(function(d) {
      return {date: d.date, value: d[key] };
    });
  });

  export default {
    name: 'AdverseEventsChart',
    mixins: [embeddable],
    data() {
      return {
        data,
        formattedData,
        chartOptions,
        colorScale,
        width: 0,
        height: 0,
      }
    },
    computed: {
      margin() {
        const left = this.chartOptions.labelWidth,
        right = 20,
        top = 10,
        bottom = 50;
        return {left, right, top, bottom}
      },
      padded() {
        const width = this.width - this.margin.left - this.margin.right
        const height = this.height - this.margin.top - this.margin.bottom
        return {width, height}
      },
      scale() {
        const x = d3.scaleBand()
          .domain(this.formattedData[0].map(d => d.date))
          .range([0, this.padded.width])
          .padding(.25),
        y = d3.scaleLinear()
          .domain([0, d3.max(this.data, d => d.deaths + d.injuries)])
          .range([this.padded.height, 0]);
        return {x, y};
      },
    },
    mounted() {
      window.addEventListener('resize', this.onResize);
      this.onResize();
    },
    beforeDestroy() {
      window.removeEventListener('resize', this.onResize);
    },
    watch: {
      width: function widthChanged() {
        this.initialize();
        this.update();
      }
    },
    methods: {
      onResize() {
        this.width = this.$el.offsetWidth;
        this.height = this.$el.offsetWidth * this.baseHeightRatio;
      },
      initialize() {
      },
      update() {
        d3.select(this.$el).select(".axis--x")
          .call(d3.axisBottom(this.scale.x)
            .tickFormat(d => castCall(this.xAxisYearFormat, d)))
          .select(".domain").remove();

        const ticks = this.chartOptions.yAxisTicks,
          tickFormat = this.chartOptions.yAxisTickFormat;

        d3.select(this.$el).select(".axis--y")
          .call(d3.axisLeft(this.scale.y)
          .tickFormat(d => tickFormat(d))
          .ticks(ticks))
          .selectAll(".tick line").attr("x2", this.padded.width);
        }
    }
  }
</script>
